/**
 * @file src/api/company.api.js
 * @description Company, selected-company context, and audit-log API calls.
 *
 * Backend derives companyId from the selected-company httpOnly cookie.
 * Frontend must not send companyId for company-scoped resources.
 */

import { apiClient } from "lib/axios";

// ---------------------------------------------------------------------------
// Company context
// ---------------------------------------------------------------------------

export const getMyCompanies = async () => {
  const { data } = await apiClient.get("/companies/mine");
  return data;
};

export const selectCompany = async (companyId) => {
  const { data } = await apiClient.post("/companies/select", { companyId });
  return data;
};

export const getActiveCompany = async () => {
  const { data } = await apiClient.get("/companies/active");
  return data;
};

// ---------------------------------------------------------------------------
// Company settings
// ---------------------------------------------------------------------------

export const getCompany = async () => {
  const { data } = await apiClient.get("/auth/get-company");
  return data;
};

export const createCompany = async (body) => {
  const companyPayload = {
    legalName: body.legalName,
    vatNumber: body.vatNumber,
    registrationNumber: {
      id: body.registrationNumber.id,
      schemeId: body.registrationNumber.schemeId,
    },
    buildingNumber: body.buildingNumber,
    street: body.street,
    district: body.district,
    city: body.city,
    countrySubentity: body.countrySubentity,
    postalCode: body.postalCode,
    country: body.country,
    ...(body.plotIdentification && {
      plotIdentification: body.plotIdentification,
    }),
    ...(body.phone && { phone: body.phone }),
    ...(body.email && { email: body.email }),
  };

  if (body.logo && body.logo.startsWith("data:")) {
    const formData = new FormData();
    formData.append("action", "create");
    formData.append("company", JSON.stringify(companyPayload));

    const fetchResponse = await fetch(body.logo);
    const blob = await fetchResponse.blob();
    formData.append("logo", blob, "logo.png");

    const { data } = await apiClient.post("/auth/company", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return data;
  }

  const { data } = await apiClient.post("/auth/company", {
    action: "create",
    company: companyPayload,
  });

  return data;
};

export const updateCompany = async (body) => {
  const { companyId, id, action, ...cleanBody } = body;
  const hasLogo = cleanBody.logo && cleanBody.logo.startsWith("data:");

  if (hasLogo) {
    const formData = new FormData();

    Object.entries(cleanBody).forEach(([key, value]) => {
      if (key === "logo") return;

      if (value !== null && value !== undefined) {
        formData.append(
          key,
          typeof value === "object" ? JSON.stringify(value) : value,
        );
      }
    });

    const fetchResponse = await fetch(cleanBody.logo);
    const blob = await fetchResponse.blob();
    formData.append("logo", blob, "logo.png");

    const { data } = await apiClient.put("/auth/update-company", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return data;
  }

  const { data } = await apiClient.put("/auth/update-company", cleanBody);
  return data;
};

// ---------------------------------------------------------------------------
// Audit logs
// ---------------------------------------------------------------------------

export const getAuditLogs = async ({ page = 1, limit = 10 } = {}) => {
  const { data } = await apiClient.get("/auth/audit-logs", {
    params: { page, limit },
  });

  return data;
};
